/**
 * HR Teams management — /hr/teams (and the Org Structure "Teams" tab via
 * <HrTeamsPageInner embedded/>).
 *
 * Team CRUD under HR control: create a team, edit it (name / manager / org
 * unit) through a modal, and delete an empty team. Row actions live behind a
 * kebab (⋮) menu, matching the rest of the app. Deleting a team with members
 * is refused by the API (409) and surfaced as a toast.
 *
 * Gated on `flags.hrTeamsManagement`. Backed by POST/PATCH/DELETE
 * /api/v1/admin/teams/{id} + GET /api/v1/teams (all audit-logged).
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { OrgUnitType, Team } from '@lms/shared-types'
import { PageHead } from '../components/primitives/PageHead'
import { Card } from '../components/primitives/Card'
import { Button } from '../components/primitives/Button'
import { Combobox } from '../components/primitives/Combobox'
import { Modal } from '../components/primitives/Modal'
import { RowActionsMenu } from '../components/primitives/RowActionsMenu'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { useToast } from '../components/primitives/Toast'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { ApiError } from '../api/client'
import { listTeams } from '../api/teams'
import { createTeam, deleteTeam, updateTeam } from '../api/admin-teams'
import { updateEmployee } from '../api/employees'
import { getOrgChart } from '../api/hr-org-chart'
import { UnitTypeChip } from '../components/hr/org-chart/UnitTypeChip'
import { flags } from '../lib/feature-flags'

interface MemberCandidate {
  id: string
  name: string
  jobTitle: string
  teamId: string
  teamName: string
}

interface UnitOption {
  id: string
  name: string
  unitType: OrgUnitType
}
interface EmpOption {
  id: string
  name: string
  jobTitle: string
}

const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 14,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
}
const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 12,
  color: 'var(--text-2)',
}

// ── helpers ──────────────────────────────────────────────────────────────────

function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.message) return err.message
  if (err instanceof Error) return err.message
  return fallback
}

/** Build a team-id → manager display-name map from the org chart. */
function buildManagerNames(
  teams: ReadonlyArray<Team>,
  employees: ReadonlyArray<{
    id: string
    name: string
    teamId: string
    isManager: boolean
  }>,
): Map<string, string> {
  const out = new Map<string, string>()
  for (const team of teams) {
    const byId = team.managerId
      ? employees.find((e) => e.id === team.managerId)
      : undefined
    const manager =
      byId ?? employees.find((e) => e.teamId === team.id && e.isManager)
    if (manager) out.set(team.id, manager.name)
  }
  return out
}

// ── Edit team modal (name / manager / org unit) ───────────────────────────────

function EditTeamModal({
  open,
  onClose,
  team,
  currentUnitId,
  units,
  employees,
}: {
  open: boolean
  onClose: () => void
  team: Team | null
  currentUnitId: string | null
  units: UnitOption[]
  employees: EmpOption[]
}): JSX.Element {
  const queryClient = useQueryClient()
  const { notify } = useToast()
  const [name, setName] = useState('')
  const [managerId, setManagerId] = useState('')
  const [unitId, setUnitId] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open && team) {
      setName(team.name)
      setManagerId(team.managerId ?? '')
      setUnitId(currentUnitId ?? '')
      setError(null)
    }
  }, [open, team, currentUnitId])

  const mutation = useMutation({
    mutationFn: () =>
      updateTeam(team!.id, {
        name: name.trim(),
        managerId: managerId || null,
        parentUnitId: unitId || null,
      }),
    onSuccess: async (updated) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['teams'] }),
        queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] }),
      ])
      notify(`Saved “${updated.name}”`, 'success')
      onClose()
    },
    onError: (err: unknown) => setError(apiErrorMessage(err, 'Could not save team')),
  })

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault()
    setError(null)
    if (name.trim().length === 0) {
      setError('Team name is required.')
      return
    }
    mutation.mutate()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit team"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="edit-team-form"
            loading={mutation.isPending}
            data-testid="edit-team-save"
          >
            Save
          </Button>
        </>
      }
    >
      <form
        id="edit-team-form"
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <label style={labelStyle}>
          <span>Team name</span>
          <input
            data-testid="edit-team-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={120}
            style={fieldStyle}
          />
        </label>

        <label style={labelStyle}>
          <span>Manager</span>
          <Combobox
            inputTestId="edit-team-manager"
            ariaLabel="Manager"
            placeholder="Search an employee…"
            emptyOptionLabel="— No manager —"
            value={managerId}
            onChange={setManagerId}
            options={employees.map((e) => ({
              value: e.id,
              label: e.name,
              sublabel: e.jobTitle,
            }))}
          />
        </label>

        <label style={labelStyle}>
          <span>Org unit</span>
          <Combobox
            inputTestId="edit-team-unit"
            ariaLabel="Org unit"
            placeholder="Search a unit…"
            emptyOptionLabel="— Unassigned —"
            value={unitId}
            onChange={setUnitId}
            options={units.map((u) => ({
              value: u.id,
              label: u.name,
              badge: <UnitTypeChip unitType={u.unitType} />,
            }))}
          />
        </label>

        {error ? (
          <p
            role="alert"
            data-testid="edit-team-error"
            style={{ fontSize: 13, color: 'var(--danger-fg)', margin: 0 }}
          >
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}

// ── Manage members modal ──────────────────────────────────────────────────
// Current members are pre-checked. Uncheck to remove (→ the "Unassigned" team,
// since every employee must belong to exactly one team) and check others to
// add (moving them here). Save applies both directions via updateEmployee.

const UNASSIGNED_TEAM_NAME = 'Unassigned'

function ManageMembersModal({
  open,
  onClose,
  team,
  candidates,
  teams,
}: {
  open: boolean
  onClose: () => void
  team: Team | null
  /** All (non-system) employees with their current team. */
  candidates: MemberCandidate[]
  /** Existing teams — used to find (or create) the Unassigned parking team. */
  teams: Team[]
}): JSX.Element {
  const queryClient = useQueryClient()
  const { notify } = useToast()
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)

  const currentIds = useMemo(
    () => new Set(candidates.filter((c) => c.teamId === team?.id).map((c) => c.id)),
    [candidates, team],
  )

  useEffect(() => {
    if (open) {
      setQuery('')
      setSelected(new Set(currentIds))
      setError(null)
    }
  }, [open, currentIds])

  const q = query.trim().toLowerCase()
  const matches = (c: MemberCandidate): boolean =>
    !q || c.name.toLowerCase().includes(q) || c.jobTitle.toLowerCase().includes(q)

  // Current members always shown (uncheck to remove); other employees appear
  // only while searching (check to add).
  const members = candidates.filter((c) => currentIds.has(c.id) && matches(c))
  const others = q
    ? candidates.filter((c) => !currentIds.has(c.id) && matches(c))
    : []

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toAdd = [...selected].filter((id) => !currentIds.has(id))
  const toRemove = [...currentIds].filter((id) => !selected.has(id))
  const changeCount = toAdd.length + toRemove.length

  const mutation = useMutation({
    mutationFn: async () => {
      // Removals need a destination team (team_id is NOT NULL) — the shared
      // "Unassigned" parking team, found by name or created on first use.
      let unassignedId: string | null = null
      if (toRemove.length > 0) {
        const existing = teams.find(
          (t) => t.name.toLowerCase() === UNASSIGNED_TEAM_NAME.toLowerCase(),
        )
        unassignedId = existing
          ? existing.id
          : (await createTeam(UNASSIGNED_TEAM_NAME, null, null)).id
      }
      await Promise.all([
        ...toAdd.map((id) => updateEmployee(id, { teamId: team!.id })),
        ...toRemove.map((id) => updateEmployee(id, { teamId: unassignedId! })),
      ])
      return { added: toAdd.length, removed: toRemove.length }
    },
    onSuccess: async ({ added, removed }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['teams'] }),
        queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] }),
        queryClient.invalidateQueries({ queryKey: ['employees'] }),
      ])
      const parts = [
        added ? `${added} added` : '',
        removed ? `${removed} moved to Unassigned` : '',
      ].filter(Boolean)
      notify(`Updated “${team?.name}” — ${parts.join(', ')}`, 'success')
      onClose()
    },
    onError: (err: unknown) =>
      setError(apiErrorMessage(err, 'Could not update members')),
  })

  function Row({ c }: { c: MemberCandidate }): JSX.Element {
    return (
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '7px 8px',
          borderRadius: 8,
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          data-testid={`manage-member-${c.id}`}
          checked={selected.has(c.id)}
          onChange={() => toggle(c.id)}
        />
        <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontSize: 13, color: 'var(--text)' }}>{c.name}</span>
          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
            {c.jobTitle ? `${c.jobTitle} · ` : ''}
            {c.teamId === team?.id ? 'in this team' : `currently in ${c.teamName}`}
          </span>
        </span>
      </label>
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={team ? `Manage members of “${team.name}”` : 'Manage members'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            data-testid="manage-members-save"
            loading={mutation.isPending}
            disabled={changeCount === 0}
            onClick={() => mutation.mutate()}
          >
            {changeCount > 0 ? `Save (${changeCount})` : 'Save'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-2)' }}>
          Uncheck a member to remove them (they move to “Unassigned”). Search to
          find and add others — adding moves them from their current team.
        </p>
        <input
          data-testid="manage-members-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search to add someone…"
          style={fieldStyle}
        />
        <div
          style={{
            maxHeight: 320,
            overflowY: 'auto',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 4,
          }}
        >
          {members.length === 0 && others.length === 0 ? (
            <p style={{ margin: 0, padding: 12, fontSize: 13, color: 'var(--text-3)' }}>
              {q ? 'No matching employees.' : 'No members yet — search to add someone.'}
            </p>
          ) : (
            <>
              {members.map((c) => (
                <Row key={c.id} c={c} />
              ))}
              {others.length > 0 ? (
                <>
                  <div
                    style={{
                      padding: '6px 8px 2px',
                      fontSize: 10.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      color: 'var(--text-3)',
                    }}
                  >
                    Add to this team
                  </div>
                  {others.map((c) => (
                    <Row key={c.id} c={c} />
                  ))}
                </>
              ) : null}
            </>
          )}
        </div>
        {error ? (
          <p
            role="alert"
            data-testid="manage-members-error"
            style={{ fontSize: 13, color: 'var(--danger-fg)', margin: 0 }}
          >
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}

// ── create-team row ──────────────────────────────────────────────────────────

function CreateTeamRow({
  units,
  employees,
}: {
  units: UnitOption[]
  employees: EmpOption[]
}): JSX.Element {
  const queryClient = useQueryClient()
  const { notify } = useToast()
  const [name, setName] = useState<string>('')
  const [unitId, setUnitId] = useState<string>('')
  const [managerId, setManagerId] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => createTeam(name.trim(), unitId || null, managerId || null),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['teams'] })
      await queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] })
      notify(`Created team “${created.name}”`, 'success')
      setName('')
      setUnitId('')
      setManagerId('')
      setError(null)
    },
    onError: (err: unknown) => {
      setError(apiErrorMessage(err, 'Failed to create team'))
    },
  })

  function submit(): void {
    setError(null)
    if (name.trim().length === 0) {
      setError('Team name is required.')
      return
    }
    mutation.mutate()
  }

  return (
    <Card padding={16} style={{ marginBottom: 16 }}>
      <div
        style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <input
          data-testid="team-create-input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
          placeholder="New team name"
          aria-label="New team name"
          maxLength={120}
          style={{
            ...fieldStyle,
            flex: '1 1 240px',
            minWidth: 200,
            border: error ? '1.5px solid var(--danger-line)' : fieldStyle.border,
          }}
        />
        <Combobox
          inputTestId="team-create-manager"
          ariaLabel="Manager"
          placeholder="Manager (optional)"
          emptyOptionLabel="— No manager —"
          value={managerId}
          onChange={setManagerId}
          options={employees.map((e) => ({
            value: e.id,
            label: e.name,
            sublabel: e.jobTitle,
          }))}
          style={{ flex: '0 1 200px' }}
        />
        <Combobox
          inputTestId="team-create-unit"
          ariaLabel="Org unit"
          placeholder="Org unit (optional)"
          emptyOptionLabel="— No unit —"
          value={unitId}
          onChange={setUnitId}
          options={units.map((u) => ({
            value: u.id,
            label: u.name,
            badge: <UnitTypeChip unitType={u.unitType} />,
          }))}
          style={{ flex: '0 1 200px' }}
        />
        <Button
          variant="primary"
          type="button"
          onClick={submit}
          loading={mutation.isPending}
          data-testid="team-create-submit"
        >
          + Add team
        </Button>
      </div>
      {error ? (
        <p
          role="alert"
          data-testid="team-create-error"
          style={{ fontSize: 12, color: 'var(--danger-fg)', margin: '8px 0 0' }}
        >
          {error}
        </p>
      ) : null}
    </Card>
  )
}

// ── page ─────────────────────────────────────────────────────────────────────

export function HrTeamsPage(): JSX.Element | null {
  if (!flags.hrTeamsManagement) return null
  return (
    <HrAccessGuard>
      <HrTeamsPageInner />
    </HrAccessGuard>
  )
}

export function HrTeamsPageInner({
  embedded = false,
}: {
  /** When true (rendered inside the Org Structure "Teams" tab) the page's
   *  own PageHead is omitted — the host page supplies the heading. */
  embedded?: boolean
} = {}): JSX.Element {
  const { notify } = useToast()
  const [editTarget, setEditTarget] = useState<Team | null>(null)
  const [membersTarget, setMembersTarget] = useState<Team | null>(null)

  const teamsQuery = useQuery({ queryKey: ['teams'], queryFn: listTeams })

  // Org chart resolves manager names + the unit list/assignment; same cache
  // entry the org-chart + structure pages use.
  const orgQuery = useQuery({
    queryKey: ['hr', 'org-chart'],
    queryFn: ({ signal }) => getOrgChart(signal),
    staleTime: 5 * 60_000,
  })

  const managerNames = useMemo(
    () =>
      teamsQuery.data
        ? buildManagerNames(teamsQuery.data, orgQuery.data?.employees ?? [])
        : new Map<string, string>(),
    [teamsQuery.data, orgQuery.data],
  )
  const units = useMemo<UnitOption[]>(
    () =>
      [...(orgQuery.data?.orgUnits ?? [])]
        .map((u) => ({ id: u.id, name: u.name, unitType: u.unitType }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [orgQuery.data],
  )
  const employees = useMemo<EmpOption[]>(
    () =>
      [...(orgQuery.data?.employees ?? [])]
        .map((e) => ({ id: e.id, name: e.name, jobTitle: e.jobTitle }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [orgQuery.data],
  )
  const memberCandidates = useMemo<MemberCandidate[]>(
    () =>
      [...(orgQuery.data?.employees ?? [])]
        .map((e) => ({
          id: e.id,
          name: e.name,
          jobTitle: e.jobTitle,
          teamId: e.teamId,
          teamName: e.teamName,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [orgQuery.data],
  )
  const unitNameById = useMemo(
    () => new Map((orgQuery.data?.orgUnits ?? []).map((u) => [u.id, u.name])),
    [orgQuery.data],
  )
  const teamUnitById = useMemo(() => {
    const m = new Map<string, string | null>()
    for (const t of orgQuery.data?.teams ?? []) m.set(t.id, t.parentUnitId)
    return m
  }, [orgQuery.data])

  const queryClient = useQueryClient()
  const deleteMutation = useMutation({
    mutationFn: (teamId: string) => deleteTeam(teamId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['teams'] })
      await queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] })
      notify('Team deleted', 'success')
    },
    onError: (err: unknown) => {
      notify(`Couldn't delete — ${apiErrorMessage(err, 'failed')}`, 'error')
    },
  })

  function handleDelete(team: Team): void {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the “${team.name}” team?`)) return
    deleteMutation.mutate(team.id)
  }

  const teams = teamsQuery.data ?? []
  const isLoading = teamsQuery.isLoading
  const isError = teamsQuery.isError

  const thStyle: React.CSSProperties = {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-3)',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  }
  const tdStyle: React.CSSProperties = {
    padding: '12px 14px',
    fontSize: 14,
    color: 'var(--text)',
  }

  return (
    <>
      {embedded ? null : (
        <PageHead
          eyebrow="HR · Configuration"
          title="Teams"
          subtitle="Create teams, edit their name / manager / org unit, and remove empty ones."
        />
      )}

      <CreateTeamRow units={units} employees={employees} />

      {isError ? (
        <div
          role="alert"
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'var(--danger-bg)',
            color: 'var(--danger-fg)',
            border: '1px solid var(--danger-line)',
            fontSize: 13,
          }}
        >
          Failed to load teams. Refresh the page to try again.
        </div>
      ) : null}

      <Card padding={0} style={{ overflow: 'hidden', marginBottom: 16 }}>
        <table
          data-testid="hr-teams-table"
          style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
        >
          <colgroup>
            <col style={{ width: '28%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '14%' }} />
          </colgroup>
          <thead>
            <tr
              style={{
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface-2)',
              }}
            >
              <th style={thStyle}>Team</th>
              <th style={thStyle}>Headcount</th>
              <th style={thStyle}>Manager</th>
              <th style={thStyle}>Org unit</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} style={{ padding: 32, textAlign: 'center' }}>
                  <ThinkingDots label="Loading teams" />
                </td>
              </tr>
            ) : teams.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    padding: 32,
                    textAlign: 'center',
                    color: 'var(--text-3)',
                    fontSize: 14,
                  }}
                >
                  No teams yet. Add one above.
                </td>
              </tr>
            ) : (
              teams.map((team, i) => {
                const managerName = managerNames.get(team.id) ?? '—'
                const unitId = teamUnitById.get(team.id) ?? null
                const unitName = unitId ? (unitNameById.get(unitId) ?? '—') : '—'
                const isDeleting =
                  deleteMutation.isPending && deleteMutation.variables === team.id
                return (
                  <tr
                    key={team.id}
                    style={{
                      borderBottom:
                        i < teams.length - 1
                          ? '1px solid var(--border)'
                          : undefined,
                      background: isDeleting ? 'var(--surface-2)' : undefined,
                    }}
                  >
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{team.name}</td>
                    <td
                      style={{
                        ...tdStyle,
                        fontVariantNumeric: 'tabular-nums',
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--text-2)',
                      }}
                    >
                      {team.members}
                    </td>
                    <td style={{ ...tdStyle, color: 'var(--text-2)' }}>
                      {managerName}
                    </td>
                    <td style={{ ...tdStyle, color: 'var(--text-2)' }}>
                      {unitName}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <RowActionsMenu
                        ariaLabel={`Actions for ${team.name}`}
                        testId={`team-actions-${team.id}`}
                        actions={[
                          {
                            icon: 'group',
                            label: 'Manage members',
                            testId: `team-manage-members-${team.id}`,
                            disabled: isDeleting,
                            onClick: () => setMembersTarget(team),
                          },
                          {
                            icon: 'edit',
                            label: 'Edit',
                            testId: `team-edit-${team.id}`,
                            disabled: isDeleting,
                            onClick: () => setEditTarget(team),
                          },
                          {
                            icon: 'delete',
                            label: 'Delete',
                            testId: `team-delete-${team.id}`,
                            destructive: true,
                            disabled: isDeleting,
                            onClick: () => handleDelete(team),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </Card>

      <Card padding={20}>
        <h3 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600 }}>
          Coverage tuning
        </h3>
        <p
          style={{
            margin: '0 0 10px',
            fontSize: 12.5,
            color: 'var(--text-2)',
            lineHeight: 1.6,
          }}
        >
          Per-team absence caps and minimum-present floors live on the Coverage
          thresholds page.
        </p>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={() => {
            window.location.assign('/hr/coverage')
          }}
          data-testid="teams-goto-coverage"
        >
          Open coverage thresholds
        </Button>
      </Card>

      <EditTeamModal
        open={editTarget !== null}
        onClose={() => setEditTarget(null)}
        team={editTarget}
        currentUnitId={editTarget ? (teamUnitById.get(editTarget.id) ?? null) : null}
        units={units}
        employees={employees}
      />

      <ManageMembersModal
        open={membersTarget !== null}
        onClose={() => setMembersTarget(null)}
        team={membersTarget}
        candidates={memberCandidates}
        teams={teams}
      />
    </>
  )
}
